<template>
  <div>
    <div class="px-6 py-4 analytics-sticky bg-slate-300">
      <div class="flex items-center justify-between">
        <BlTextTitle3 text="Appointment Overview" />
        <BlInputButton
          type="outlined"
          label="Export to csv"
          :loading="exportLoading"
          @clicked="onExportCSV"
        />
      </div>
      <div class="flex items-center gap-2 py-3">
        <div v-if="enableBranch" class="w-40">
          <BlSelectBox
            type="branch"
            v-model="branchInput"
            :options="branchOptions"
            @change="onChangeBranch($event)"
          />
        </div>
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <BlInputFilter
          class="w-40"
          :input="filterInput"
          @changed="onChangeFilter"
        />
        <BlInputSelect :input="groupByInput" @changed="onChangeGroupBy" />
      </div>
      <div class="flex flex-col gap-2 pt-4">
        <div>
          <BlTextBody2 text="Showing results for " />
          <BlTextSubtitle3 :text="activeDateRange" />
        </div>
        <div
          class="flex gap-5 pb-4 overflow-x-auto overflow-y-hidden scrollbar-lg"
        >
          <div
            class="flex-shrink-0 rounded-3xl bg-pure-white"
            :style="{ height: '25rem', width: '10rem' }"
          >
            <div
              v-if="fetchGraphLoading"
              class="grid h-full place-content-center"
            >
              <div class="blum-loader"></div>
            </div>
            <div
              v-else-if="!graphData"
              class="grid flex-col h-full text-center place-content-center"
            >
              <BlTextBody2
                :text="emptyState.title"
                class="px-4 font-semibold"
              />
            </div>
            <div
              v-else
              class="flex flex-col items-center justify-center h-full py-4"
            >
              <div
                v-for="item in overviewData"
                :key="item.label"
                class="w-full"
              >
                <BlTooltip
                  :tooltip="
                    `<div class='flex flex-col gap-1 text-left w-60'>
                      <b>${item.tooltip.title}</b>
                      <div>${item.tooltip.desc}</div>
                    </div>`
                  "
                  drop="right"
                  class="flex flex-col w-full px-4"
                >
                  <BlTextTitle3
                    :text="item.value"
                    class="self-center text-slate-900"
                  />
                  <div class="text-center truncate">
                    <BlTextBody2 :text="item.label" class="truncate" />
                  </div>
                </BlTooltip>
                <div v-if="item.divider" class="px-4 py-3">
                  <div class="h-0.5 flex-grow flex-shrink-0 bg-slate-300"></div>
                </div>
              </div>
            </div>
          </div>
          <!--  -->
          <div
            v-if="groupByTime"
            class="flex-grow flex-shrink-0 rounded-3xl bg-pure-white"
            :style="{ height: '25rem', width: 'auto' }"
          >
            <div
              v-if="fetchGraphLoading"
              class="grid h-full place-content-center"
            >
              <div class="blum-loader"></div>
            </div>
            <div
              v-else-if="barChartDataAppointment.empty"
              class="grid flex-col h-full text-center place-content-center"
            >
              <BlTextBody2
                :text="emptyState.title"
                class="px-4 font-semibold"
              />
              <BlTextBody2
                v-for="(text, idx) in emptyState.desc"
                :key="idx"
                :text="text"
                class="px-4"
              />
            </div>
            <div v-else class="flex flex-col gap-4 px-6 py-6">
              <BlTextSubtitle2
                text="Appointments by Hours"
                class="text-slate-700"
              />
              <BarChart
                :labels="barChartDataAppointment.labels"
                :datasets="barChartDataAppointment.datasets"
                stacked
                width="calc((100vw - 15rem - 3.5rem) - 10rem - 1.25rem - 3rem - 1.25rem - 13rem)"
              />
              <div class="flex items-center gap-5">
                <div
                  v-for="legend in barChartDataAppointment.legends"
                  :key="legend.label"
                  :title="legend.label"
                  class="flex items-center gap-1"
                >
                  <div
                    class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                    :style="{ backgroundColor: legend.color }"
                  ></div>
                  <BlTextBody2 :text="legend.label" />
                </div>
              </div>
            </div>
          </div>
          <!--  -->
          <div
            v-if="
              groupByInput.model.value === 'client' ||
                groupByInput.model.value === 'user'
            "
            class="flex-grow flex-shrink-0 rounded-3xl bg-pure-white"
            :style="{ height: '25rem', width: 'auto' }"
          >
            <div
              v-if="fetchGraphLoading"
              class="grid h-full place-content-center"
            >
              <div class="blum-loader"></div>
            </div>
            <div
              v-else-if="barChartDataRole.empty"
              class="grid flex-col h-full text-center place-content-center"
            >
              <BlTextBody2
                :text="emptyState.title"
                class="px-4 font-semibold"
              />
              <BlTextBody2
                v-for="(text, idx) in emptyState.desc"
                :key="idx"
                :text="text"
                class="px-4"
              />
            </div>
            <div v-else class="flex flex-col gap-4 px-6 py-6">
              <BlTextSubtitle2
                text="Appointments by Hours"
                class="text-slate-700"
              />
              <BarChart
                :labels="barChartDataRole.labels"
                :datasets="barChartDataRole.datasets"
                horizontal
                stacked
                :width="
                  groupByInput.model.value === 'user'
                    ? 'calc((100vw - 15rem - 3.5rem) - 10rem - 1.25rem - 3rem)'
                    : 'calc((100vw - 15rem - 3.5rem) - 10rem - 1.25rem - 3rem - 1.25rem - 13rem)'
                "
              />
              <div class="h-5.5 flex items-end justify-between gap-5">
                <div class="flex items-center gap-5">
                  <div
                    v-for="legend in barChartDataRole.legends"
                    :key="legend.label"
                    :title="legend.label"
                    class="flex items-center gap-1"
                  >
                    <div
                      class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                      :style="{ backgroundColor: legend.color }"
                    ></div>
                    <BlTextBody2 :text="legend.label" />
                  </div>
                </div>
                <BlInputPagination
                  v-if="barChartDataTotalCount > barChartPerPage"
                  :count="Math.ceil(barChartDataTotalCount / barChartPerPage)"
                  :page="barChartPage"
                  @changed="changeBarChartPage"
                />
              </div>
            </div>
          </div>
          <!--  -->
          <div
            v-if="groupByInput.model.value !== 'user'"
            class="flex-shrink-0 rounded-3xl bg-pure-white"
            :style="{ height: '25rem', width: '13rem' }"
          >
            <div
              v-if="fetchGraphLoading"
              class="grid h-full place-content-center"
            >
              <div class="blum-loader"></div>
            </div>
            <div
              v-else-if="!graphData"
              class="grid flex-col h-full text-center place-content-center"
            >
              <BlTextBody2
                :text="emptyState.title"
                class="px-4 font-semibold"
              />
            </div>
            <div v-else class="flex flex-col gap-6 px-6 py-6">
              <div class="h-12">
                <BlTextSubtitle2
                  text="Distribution of session durations"
                  class="text-slate-700"
                />
              </div>
              <PieChart
                :labels="pieChartData.labels"
                :colors="pieChartData.colors"
                :data="pieChartData.data"
              />
              <div class="flex flex-col gap-3 h-15">
                <div
                  v-for="legend in pieChartLegends"
                  :key="legend.label"
                  class="flex items-center justify-between gap-2"
                >
                  <div
                    class="flex items-center gap-1 truncate"
                    :title="legend.label"
                  >
                    <div
                      class="h-3.5 w-3.5 flex-shrink-0 rounded-full"
                      :style="{ backgroundColor: legend.color }"
                    ></div>
                    <BlTextBody2 :text="legend.label" class="truncate" />
                  </div>
                  <div>
                    <BlTextSubtitle3
                      :text="legend.value"
                      class="text-slate-800"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="pieChartPageCount > 1"
                class="flex items-center justify-center"
              >
                <div
                  v-for="n in pieChartPageCount"
                  :key="n"
                  @click="pieChartPage = n"
                  class="p-1 cursor-pointer"
                >
                  <div
                    :class="[
                      pieChartPage === n ? 'bg-slate-700' : 'bg-slate-500'
                    ]"
                    class="h-2.5 w-2.5 flex-shrink-0 rounded-full hover:bg-slate-700 transition-all duration-300"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!--  -->
    <div>
      <div
        class="z-10 flex items-center justify-between px-6 py-4 analytics-sticky top-18 bg-slate-100"
      >
        <div>
          <BlTextBody2 :text="`Showing all ${tableCount} `" />
          <BlTextBody2 v-if="groupByTime" text="session(s)" />
          <BlTextBody2
            v-else-if="groupByInput.model.value === 'client'"
            text="client(s)"
          />
          <BlTextBody2
            v-else-if="groupByInput.model.value === 'user'"
            text="member(s)"
          />
        </div>
        <BlSort
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort"
          dropDirection="left"
        />
      </div>
      <div
        v-if="fetchTableLoading"
        class="grid h-64 analytics-sticky place-content-center"
      >
        <div class="blum-loader"></div>
      </div>
      <TableAppointmentOverview
        v-else-if="groupByTime"
        :tableData="tableData"
      />
      <TableAppointmentClient
        v-else-if="groupByInput.model.value === 'client'"
        :tableData="tableData"
      />
      <TableAppointmentTherapist
        v-else-if="groupByInput.model.value === 'user'"
        :tableData="tableData"
      />
      <div class="px-6 pt-4 analytics-sticky pb-36">
        <BlInputPagination
          v-if="tableCount > 25"
          :page="page"
          :perPage="perPage"
          :total="tableCount"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import moment from "moment";

import BarChart from "./graphs/BarChart.vue";
import PieChart from "./graphs/PieChart.vue";
import TableAppointmentOverview from "./table/TableAppointmentOverview.vue";
import TableAppointmentClient from "./table/TableAppointmentClient.vue";
import TableAppointmentTherapist from "./table/TableAppointmentTherapist.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlSort from "../BlSort.vue";
import BlTooltip from "../BlTooltip.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "AppointmentOverview",
  components: {
    BarChart,
    PieChart,
    TableAppointmentOverview,
    TableAppointmentClient,
    TableAppointmentTherapist,
    BlTextTitle3,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlSort,
    BlTooltip,
    BlSelectBox,
    BlInputSearch,
    BlInputFilter,
    BlInputSelect,
    BlInputButton,
    BlInputPagination
  },
  data() {
    const interval = "week";
    const defaultDate = {
      start_date: moment()
        .startOf(interval)
        .format("YYYY-MM-DD"),
      end_date: moment()
        .endOf(interval)
        .format("YYYY-MM-DD")
    };
    const defaultGroupBy = { name: "Day", value: "day" };
    const defaultSort = { primary: "date", secondary: "asc" };
    const defaultClientSort = { primary: "client_name", secondary: "asc" };
    const defaultUserSort = { primary: "user_name", secondary: "asc" };

    return {
      defaultDate,
      defaultGroupBy,
      defaultSort,
      defaultClientSort,
      defaultUserSort,
      /** */
      fetchGraphLoading: false,
      graphData: null,
      /** */
      fetchTableLoading: false,
      tableData: [],
      tableCount: 0,
      /** */
      emptyState: {
        title: "No appointments recorded yet",
        desc: [
          "It looks like you've selected a future date range. Since no appointments have been completed or cancelled yet, there's no data to display.",
          "Try selecting a date range that includes past or current dates to view your appointment history."
        ]
      },
      /** */
      exportLoading: false,
      /** */
      branchInput: 0,
      filterInput: {
        model: [defaultDate, [], []],
        tabs: [
          { name: "Date", kind: "date" },
          {
            name: "Status",
            kind: "multi-tag",
            options: [
              {
                name: "Scheduled",
                value: "scheduled",
                color: "#FB6514",
                backgroundColor: "#FFF6ED"
              },
              {
                name: "Completed",
                value: "completed",
                color: "#789719",
                backgroundColor: "#F2F8CF"
              },
              {
                name: "Cancelled",
                value: "cancelled",
                color: "#F63D68",
                backgroundColor: "#FFF1F3"
              }
            ]
          },
          { name: "Tags", kind: "multi-tag", options: [] }
        ]
      },
      groupByInput: {
        model: defaultGroupBy,
        prefix: "Group by",
        combine: true,
        options: [
          { name: "Day", value: "day", disabled: false },
          { name: "Week", value: "week", disabled: false },
          { name: "Month", value: "month" },
          { name: "Year", value: "year", divider: true },
          { name: "Client", value: "client" },
          { name: "Therapist/admin", value: "user" }
        ]
      },
      /** */
      barChartPage: 1,
      barChartPerPage: 10,
      /** */
      pieChartPage: 1,
      pieChartPerPage: 2,
      /** */
      searchInput: { placeholder: "Search", model: "" },
      page: 1,
      perPage: 25,
      sortInput: defaultSort
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "branches", "labels"]),
    enableSalesPipeline() {
      return this.currentCenter.enable_sales_pipeline;
    },
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    searchParams() {
      return this.$route.query || {};
    },
    paramsTab() {
      return this.$route.params.tab;
    },
    activeDateRange() {
      const start = this.searchParams["start_date"];
      const end = this.searchParams["end_date"];
      if (start && end) {
        const s = moment(start).format("DD/MM/YYYY");
        const e = moment(end).format("DD/MM/YYYY");
        const diff = moment(end).diff(moment(start), "days") + 1;
        return `${s} - ${e}, ${diff} day(s)`;
      }
      return "";
    },
    groupByTime() {
      return (
        this.groupByInput.model.value === "day" ||
        this.groupByInput.model.value === "week" ||
        this.groupByInput.model.value === "month" ||
        this.groupByInput.model.value === "year"
      );
    },
    branchOptions() {
      const allBranch = {
        value: 0,
        label: "All branches",
        class: "-mx-2"
      };
      const arr = this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
      return [allBranch, ...arr];
    },
    sortOptions() {
      const primary = [
        { value: "date", label: "Appointment date" },
        { value: "client_name", label: "Client name" },
        { value: "id", label: "ID" }
      ];
      const clientPrimary = [
        { value: "client_name", label: "Client name" },
        { value: "therapist_on_duty", label: "Therapist on duty" }
      ];
      const userPrimary = [
        { value: "user_name", label: "Therapist name" },
        { value: "clients_handled", label: "Clients handled" }
      ];
      const secondary = [
        { value: "asc", label: "Ascending" },
        { value: "desc", label: "Descending" }
      ];
      if (this.groupByInput.model.value === "client") {
        return { primary: clientPrimary, secondary };
      }
      if (this.groupByInput.model.value === "user") {
        return { primary: userPrimary, secondary };
      }
      return { primary, secondary };
    },
    getFilterParams() {
      // return start_date, end_date, status, label_ids, branch_ids
      // group_by: day | week | month | year | client | user
      let params = ``;
      const dates = this.filterInput.model.at(0) || {
        start_date: "",
        end_date: ""
      };
      const statuses = this.filterInput.model.at(1) || [];
      const tags = this.filterInput.model.at(2) || [];

      params += `?start_date=${dates.start_date ||
        this.defaultDate.start_date}`;
      params += `&end_date=${dates.end_date || this.defaultDate.end_date}`;
      if (statuses.length > 0) {
        params += `&status=${statuses.map(a => a.value).join(",")}`;
      }
      if (tags.length > 0) {
        params += `&label_ids=${tags.map(a => a.value).join(",")}`;
      }
      if (this.branchInput) {
        params += `&branch_ids=${this.branchInput}`;
      }
      params += `&group_by=${this.groupByInput.model.value}`;
      return params;
    },
    /*
    "overview": {
      "total_completed_session": 5,
      "total_completed_hours": 2.5,
      "average_completed_session": 0,
      "average_completed_hours": 0.1,
      "highest_completed_session": 3,
      "highest_completed_hours": 1.5,
      "lowest_completed_session": 2,
      "lowest_completed_hours": 1.0
    },
    */
    overviewData() {
      if (!this.graphData) return [];
      const raw = this.graphData.overview;
      if (!raw) return [];
      const keys = [
        "total_completed_hours",
        "average_completed_hours",
        "highest_completed_hours",
        "lowest_completed_hours"
      ];

      const words = {
        day: "daily",
        week: "weekly",
        month: "monthly",
        year: "yearly",
        client: "client",
        user: "therapist"
      };
      const groupBy = this.groupByInput.model.value;
      const labels = [
        this.groupByTime
          ? `Total completed sessions (hours)`
          : `Total completed sessions by ${words[groupBy]} (hours)`,
        this.groupByTime
          ? `Average ${words[groupBy]} session (hours)`
          : `Average daily session by ${words[groupBy]} (hours)`,
        this.groupByTime
          ? `Highest ${words[groupBy]} total (hours)`
          : `Highest session hours by ${words[groupBy]}`,
        this.groupByTime
          ? `Lowest ${words[groupBy]} total (hours)`
          : `Lowest session hours by ${words[groupBy]}`
      ];
      const tooltips = [
        this.groupByTime
          ? `Sum of all completed session hours within the selected timeframe.`
          : `Sum of session hours completed by each ${words[groupBy]} within the selected timeframe.`,
        this.groupByTime
          ? `Average total of session hours completed per ${groupBy} within the selected timeframe, excluding days with no completed sessions.`
          : `Average number of session hours completed by each ${words[groupBy]} within the selected timeframe.`,
        this.groupByTime
          ? `The highest number of session hours completed in a single ${groupBy} within the selected timeframe.`
          : `Total session hours completed by the ${words[groupBy]} with the highest session hours within the selected timeframe.`,
        this.groupByTime
          ? `The lowest number of session hours completed in a single ${groupBy} within the selected timeframe.`
          : `Total hours from the ${words[groupBy]} who completed the fewest session hours within the selected timeframe.`
      ];

      function rounded(n = 0) {
        const t = n.toString();
        return t.includes(".") ? n.toFixed(1) : n;
      }
      return keys.map((key, idx) => ({
        label: labels[idx],
        tooltip: {
          title: labels[idx],
          desc: tooltips[idx]
        },
        value: rounded(raw[key] || 0),
        divider: idx !== keys.length - 1
      }));
    },
    /*
    "data": [
      {
        "date": "2024-07-01",
        "completed_by_session": 5,
        "in_progress_by_session": 0,
        "cancelled_by_session": 5,
        "completed_by_hours": 2.5,
        "in_progress_by_hours": 0,
        "cancelled_by_hours": 4.0
        "scheduled_by_hours": 4.0
      }
    ]
     */
    barChartDataAppointment() {
      const n = { labels: [], datasets: [], legends: [], empty: true };
      if (!this.groupByTime) return n;
      if (!this.graphData) return n;
      const raw = this.graphData.data;
      if (!raw) return n;
      const keys = [
        "scheduled_by_hours",
        "completed_by_hours",
        "cancelled_by_hours"
      ];
      const colors = ["#FD853A", "#B9D843", "#F63D68"];
      const texts = ["Scheduled", "Completed", "Cancelled"];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      const legends = keys.map((key, idx) => ({
        label: texts[idx],
        color: colors[idx]
      }));
      let empty = true;

      const formats = {
        day: "DD/MM/YY",
        week: "DD/MM",
        month: "MMM YYYY",
        year: "YYYY"
      };
      const groupBy = this.groupByInput.model.value;

      raw.forEach(i => {
        let l = moment(i.date)
          .format(formats[groupBy])
          .toUpperCase();
        if (groupBy === "week") {
          l += `-${moment(i.date)
            .add(6, "days")
            .format(formats[groupBy])}`;
        }
        labels.push(l);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, legends, empty };
    },
    /*
    "data": {
      "clients": [
        {
          "id": 1,
          "client_name": "Doe",
          "client_tags": [],
          "client_branches": [],
          "hour_to_session_ratio": 2.0,
          "therapist_on_duty": 1,
          "completed_by_session": 1,
          "completed_by_hours": 2.0,
          "in_progress_by_session": 0,
          "in_progress_by_hours": 0.0,
          "cancelled_by_session": 2,
          "cancelled_by_hours": 4.0,
          "scheduled_by_session": 7,
          "scheduled_by_hours": 14.0,
          "total_appointments": 10,
          "total_appointments_duration": 20.0
        }
      ],
      "total_count": 4
    }
    "data": {
      "users": [
        {
          "id": 2,
          "user_name": "John",
          "role": "staff",
          "clients_handled": 5,
          "hour_to_session_ratio": 2.0,
          "completed_by_session": 1,
          "completed_by_hours": 2.0,
          "in_progress_by_session": 0,
          "in_progress_by_hours": 0.0,
          "cancelled_by_session": 3,
          "cancelled_by_hours": 4.5,
          "scheduled_by_session": 13,
          "scheduled_by_hours": 4.5,
          "total_appointments": 17,
          "total_appointments_duration": 26.0
        }
      ],
      "total_count": 2
    }
    */
    barChartDataRole() {
      const n = { labels: [], datasets: [], legends: [], empty: true };
      if (this.groupByTime) return n;
      if (!this.graphData) return n;
      const groupBy = this.groupByInput.model.value;
      const raw = this.graphData.data[`${groupBy}s`];
      if (!raw) return n;
      const keys = [
        "scheduled_by_hours",
        "completed_by_hours",
        "cancelled_by_hours"
      ];
      const colors = ["#FD853A", "#B9D843", "#F63D68"];
      const texts = ["Scheduled", "Completed", "Cancelled"];

      const labels = [];
      const datasets = keys.map((key, idx) => ({
        label: texts[idx],
        data: [],
        color: colors[idx]
      }));
      const legends = keys.map((key, idx) => ({
        label: texts[idx],
        color: colors[idx]
      }));
      let empty = true;

      raw.forEach(i => {
        labels.push(i[[`${groupBy}_name`]]);
        keys.forEach((key, idx) => {
          const value = i[key] || 0;
          datasets[idx].data.push(value);
          if (value) empty = false;
        });
      });

      return { labels, datasets, legends, empty };
    },
    barChartDataTotalCount() {
      if (this.groupByTime) return 0;
      if (!this.graphData) return 0;
      return this.graphData.data.total_count || 0;
    },
    /*
    "completed_session_duration": [
      {
        "duration": 0.5,
        "total": 5
      }
    ],
    */
    pieChartData() {
      const n = { labels: [], colors: [], data: [], legends: [] };
      if (!this.graphData) return n;
      let raw = this.graphData.completed_session_duration;
      if (!raw) return n;
      raw = raw.length
        ? raw
        : [0.5, 1, 1.5, 2].map(i => ({ duration: i, total: 0 }));
      const labels = raw.map(i => `${i.duration} hr`);
      const colors = [
        "#5B7417",
        "#789719",
        "#9BBE24",
        "#B9D843",
        "#D1E875",
        "#E4F2A4",
        "#F2F8CF",
        "#F9FCE9"
      ]
        .slice(0, raw.length)
        .reverse();
      const data = raw.map(i => i.total);
      const legends = raw
        .map((key, idx) => ({
          label: labels[idx],
          color: colors[idx] || "#F9FCE9",
          value: data[idx]
        }))
        .sort((a, b) => b.value - a.value);
      return { labels, colors, data, legends };
    },
    pieChartLegends() {
      const legends = this.pieChartData.legends;
      const start = (this.pieChartPage - 1) * this.pieChartPerPage;
      const end = start + this.pieChartPerPage;
      return legends.slice(start, end);
    },
    pieChartPageCount() {
      const legends = this.pieChartData.legends;
      const dataLength = legends.length;
      return Math.ceil(dataLength / this.pieChartPerPage) || 1;
    }
  },
  watch: {
    labels: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val.length) {
          this.filterInput.tabs[2].options = val.map(a => ({
            name: a.name,
            value: a.id,
            color: "#475467",
            backgroundColor: "#F2F4F7"
          }));
        }
      }
    }
    // "filterInput.model": {
    //   deep: true,
    //   immediate: true,
    //   handler(val) {
    //     const start = val[0].start_date;
    //     const end = val[0].end_date;
    //     if (start && end) {
    //       const diff = moment(end).diff(moment(start), "days") + 1;
    //       const inDayOrWeek =
    //         this.groupByInput.model.value === "day" ||
    //         this.groupByInput.model.value === "week";
    //       if (diff >= 365 && inDayOrWeek) {
    //         this.onChangeGroupBy({
    //           input: this.groupByInput,
    //           value: { name: "Month", value: "month" }
    //         });
    //       }
    //     }
    //   }
    // }
  },
  mounted() {
    let filter = [this.defaultDate, [], []];
    const searchParams = this.searchParams;

    if (searchParams["start_date"]) {
      filter[0].start_date = searchParams["start_date"];
    }
    if (searchParams["end_date"]) {
      filter[0].end_date = searchParams["end_date"];
    }
    if (searchParams["status"]) {
      const arr = searchParams["status"].split(",");
      filter[1] = arr.map(a => ({ value: a }));
    }
    if (searchParams["label_ids"]) {
      const arr = searchParams["label_ids"].split(",");
      filter[2] = arr.map(a => ({ value: Number(a) }));
    }
    this.filterInput.model = filter;
    if (searchParams["branch_ids"]) {
      this.branchInput = Number(searchParams["branch_ids"]);
    }
    if (searchParams["group_by"]) {
      const n = this.groupByInput.options.find(
        i => i.value === searchParams["group_by"]
      );
      if (n) this.groupByInput.model = n;
    }
    /** */
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["page"];
    }
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["sort_by"]) {
      const n = searchParams["sort_by"] || "";
      if (n.includes("_")) {
        const arr = n.split("_");
        const secondary = arr.pop();
        const primary = arr.join("_");
        this.sortInput = { primary, secondary };
      }
    }

    this.fetchGraph();
    this.fetchTable();
  },
  methods: {
    ...mapActions(["actionGet", "actionDownload"]),
    syncPath() {
      let params = this.getFilterParams;
      /** */
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      params += `&page=${this.page}&per_page=${this.perPage}`;
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      // sync url path for page
      const incomingPath = "/analytics/appointment-overview" + params;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    fetchGraph() {
      /*
        start_date, end_date, status, label_ids, branch_ids,
        group_by: day | week | month | year | client | user
      */
      const start = this.filterInput.model[0].start_date;
      const end = this.filterInput.model[0].end_date;
      this.groupByInput.options[0].disabled = false;
      this.groupByInput.options[1].disabled = false;
      if (start && end) {
        const diff = moment(end).diff(moment(start), "days") + 1;
        if (diff >= 365) {
          this.groupByInput.options[0].disabled = true;
          this.groupByInput.options[1].disabled = true;
          const groupByDayOrWeek =
            this.groupByInput.model.value === "day" ||
            this.groupByInput.model.value === "week";
          if (groupByDayOrWeek) {
            this.groupByInput.model = { name: "Month", value: "month" };
          }
        }
      }

      let params = this.getFilterParams;
      if (!this.groupByTime) {
        params += `&page=${this.barChartPage}&per_page=${this.barChartPerPage}`;
      }
      const data = {
        url: `/api/v1/analytics/appointment_overview_graph${params}`
      };
      this.fetchGraphLoading = true;
      this.graphData = null;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchGraphLoading = false;
        if (!success) return;
        this.graphData = data;
      });
      this.syncPath();
    },
    fetchTable() {
      /*
        start_date, end_date, status, label_ids, branch_ids,
        group_by: day | week | month | year | client | user
        --
        page, per_page,
        query,
        sort_by:
          | date_asc | date_desc
          | client_name_asc | client_name_desc
          | id_asc | id_desc
          -- group_by: client
          | client_name_asc | client_name_desc
          | therapist_on_duty_asc | therapist_on_duty_desc
          -- group_by: user
          | user_name_asc | user_name_desc
          | clients_handled_asc | clients_handled_desc
      */
      let params = this.getFilterParams;
      if (this.searchInput.model) {
        params += `&query=${this.searchInput.model}`;
      }
      params += `&page=${this.page}&per_page=${this.perPage}`;
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      const data = {
        url: `/api/v1/analytics/appointment_overview_table${params}`
      };
      this.fetchTableLoading = true;
      this.tableData = [];
      this.tableCount = 0;
      this.actionGet(data).then(({ success, data }) => {
        this.fetchTableLoading = false;
        if (!success) return;
        this.tableData = data.appointments;
        this.tableCount = data.total_count;
      });
      this.syncPath();
    },
    onExportCSV() {
      /*
        start_date, end_date, status, label_ids, branch_ids,
        group_by: day | week | month | year | client | user
        --
        sort_by:
          | date_asc | date_desc
          | client_name_asc | client_name_desc
          | id_asc | id_desc
          -- group_by: client
          | client_name_asc | client_name_desc
          | therapist_on_duty_asc | therapist_on_duty_desc
          -- group_by: user
          | user_name_asc | user_name_desc
          | clients_handled_asc | clients_handled_desc
      */
      let params = this.getFilterParams;
      params += `&sort_by=${this.sortInput.primary}_${this.sortInput.secondary}`;
      const data = {
        url: `/api/v1/analytics/appointment_overview_csv${params}`,
        title: "",
        message: ""
      };
      this.exportLoading = true;
      this.actionDownload(data).then(({ success }) => {
        this.exportLoading = false;
        // if (!success) return;
      });
    },
    /** */
    onChangeBranch(val) {
      this.branchInput = val;
      this.fetchGraph();
      this.fetchTable();
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.barChartPage = 1;
      this.fetchTable();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.barChartPage = 1;
      this.fetchGraph();
      this.fetchTable();
    },
    onChangeGroupBy(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.barChartPage = 1;
      const val = payload.value.value;
      if (val === "client") {
        this.sortInput = this.defaultClientSort;
      } else if (val === "user") {
        this.sortInput = this.defaultUserSort;
      } else {
        this.sortInput = this.defaultSort;
      }
      this.fetchGraph();
      this.fetchTable();
    },
    changeBarChartPage(page) {
      if (page === this.barChartPage) return;
      this.barChartPage = page;
      this.fetchGraph();
    },
    /** */
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchTable();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchTable();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchTable();
    }
  }
};
</script>

<style scoped lang="scss">
.analytics-sticky {
  width: calc(100vw - 15rem - 0.5rem);
  position: sticky;
  left: 15rem;
}
</style>



// WEBPACK FOOTER //
// src/components/analytics/AppointmentOverview.vue